<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Engagement' },
      { label: 'Invite Summary' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
          show-presets
          class="min-w-60"
          :presets="dateRangePresets"
          @select="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>

  <Alert
    severity="warning"
    title="Data Issue Notice"
    message="Due to a bug in the software, the data for Appointments Booked was not being collected prior to 16/01/2025. The column's data prior to this date will not be accurate."
    :show-close-button="false"
    :show-dismiss-button="false"
    class="mb-4"
  />

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Invite Summary"
      :value="inviteData"
      removable-sort
      sort-field="timestamp"
      :sort-order="-1"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['dealershipId', 'dealershipName']"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-invite-summary"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="inviteApiRequest.isLoading.value"
    >
      <template #empty>No records found</template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="dealershipName"
        header="Dealership Name"
        hidden
      />

      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="dealershipsList"
            mode="multiple"
            :chips="false"
            searchable
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="sentCount"
        header="Sent"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="`/reports/engagement/appointment-invites?dealership=${[data.dealershipId]}`"
            class="link"
          >
            {{ data.sentCount }}
          </router-link>
        </template>
      </Column>
      <Column
        field="openedCount"
        header="Opened"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.openedCount }}
        </template>
      </Column>
      <Column
        field="clickedCount"
        header="Link Clicked"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.clickedCount }}
        </template>
      </Column>
      <Column
        field="appointmentsBookedCount"
        header="Appointments Booked"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="`/reports/engagement/appointment-invites?dealership=${[data.dealershipId]}&appointment=${[data.appointmentsBookedCount ? true : false]}`"
            class="link"
          >
            {{ data.appointmentsBookedCount }}
          </router-link>
        </template>
      </Column>
      <Column
        field="appointmentsAttendedCount"
        header="Appointments Attended"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.appointmentsAttendedCount }}
        </template>
      </Column>
      <Column
        field="appointmentsCompletedCount"
        header="Appointments Dealt"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.appointmentsCompletedCount }}
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'

import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Alert from '@/components/alert/Alert.vue'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
import { useBreakPoints } from '@/composables/useWindowSize'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { alertToast } from '@/utilities/notification'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from 'primevue/api'
import dayjs from 'dayjs'

const dt = ref()
const authStore = useAuthStore()
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)
const inviteApiRequest = useApiRequest()
const inviteData = ref([])
const filters = ref([])

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const dealershipsList = computed(() => {
  return dealershipsAllowed.value.map(d => {
    return {
      value: d.id,
      label: d.name
    }
  })
})

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function fetchInviteData() {
  try {
    inviteApiRequest
      .send({
        endpoint: 'v1/reports/engagement/invites',
        params: {
          start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value[1]).format('YYYY-MM-DD'),
          dealerships: dealershipsAllowed.value.map(d => d.id).join(',')
        }
      })
      .then(response => {
        if (response.success) {
          inviteData.value = response.data
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}
fetchInviteData()

function onDateRangeChange() {
  if (dateRange.value[0] && dateRange.value[1]) {
    fetchInviteData()
  }
}

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
